import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, TableWrap, thClass, tdClass } from "@/components/insights/prose";

export const whichIsoMeta: InsightMeta = {
  slug: "which-iso-standard-does-my-company-need",
  metaTitle: {
    en: "Which ISO Standard Does My Company Need? | IraqCert",
    ar: "أي شهادة أيزو تحتاج شركتي؟ | عراق سيرت",
  },
  title: {
    en: "Which ISO Standard Does My Company Need?",
    ar: "أي شهادة أيزو تحتاج شركتي؟",
  },
  description: {
    en: "Choose an ISO standard for your activities and buyer requirements: quality, environment, workplace safety, food safety and petroleum-sector quality.",
    ar: "اختر مواصفة الأيزو المناسبة لأنشطتك ومتطلبات المشتري: الجودة والبيئة والسلامة المهنية وسلامة الغذاء وجودة سلسلة توريد النفط والغاز.",
  },
  datePublished: "2026-07-21",
  dateModified: "2026-09-29",
  dateLabel: { en: "29 September 2026", ar: "29 أيلول 2026" },
  enquiryTitle: {
    en: "Discuss the ISO standards your business needs",
    ar: "ناقش مواصفات الأيزو التي تحتاجها شركتك",
  },
  enquiryButton: {
    en: "Discuss your ISO requirements",
    ar: "ناقش احتياجات شركتك لشهادات الأيزو",
  },
  enquiryMessage: {
    en: "Hello IraqCert, I would like to discuss the ISO standards relevant to my business.\nBusiness activity and sites:\nCustomer requirement or reason for certification:\nStandards under consideration, if known:\nPage: /insights/which-iso-standard-does-my-company-need",
    ar: "مرحباً IraqCert، أرغب في مناقشة مواصفات الأيزو المناسبة لشركتي.\nنشاط الشركة ومواقعها:\nمتطلب العميل أو سبب طلب الشهادة:\nالمواصفات التي نفكر فيها، إن كانت محددة:\nالصفحة: /ar/insights/which-iso-standard-does-my-company-need",
  },
  faq: [
    {
      q: {
        en: "Can I get multiple ISO certifications at once?",
        ar: "هل يمكن الحصول على عدة شهادات أيزو معاً؟",
      },
      a: {
        en: "Yes. ISO 9001, ISO 14001 and ISO 45001 can be implemented as an integrated management system. Ask the certification body whether a combined audit is suitable; audit duration and scheduling depend on your scope and readiness.",
        ar: "نعم. يمكن تطبيق ISO 9001 وISO 14001 وISO 45001 ضمن نظام إدارة متكامل. اسأل الجهة المانحة عن ملاءمة التدقيق المشترك؛ فمدة التدقيق وجدولته يعتمدان على نطاق أنشطتك ومدى جاهزيتك.",
      },
    },
    {
      q: {
        en: "Which ISO is most important for tenders?",
        ar: "ما مواصفة الأيزو المطلوبة للمناقصات؟",
      },
      a: {
        en: "The tender documents decide. Check the required standard, certified scope, sites, validity and any accreditation conditions. ISO 9001 addresses quality management, but it is not a universal requirement for every tender.",
        ar: "تحدد وثائق المناقصة المتطلبات. راجع المواصفة المطلوبة ونطاق الشهادة والمواقع المشمولة وصلاحيتها وأي شروط تتعلق بالاعتماد. تعالج ISO 9001 إدارة الجودة، لكنها ليست شرطاً عاماً لكل مناقصة.",
      },
    },
    {
      q: {
        en: "Which ISO standards do oil and gas companies need?",
        ar: "أي معايير أيزو يحتاجها قطاع النفط والغاز؟",
      },
      a: {
        en: "ISO 9001, ISO 14001 and ISO 45001 address quality, environment and workplace safety. ISO 29001 adds petroleum-sector quality requirements. Select what fits your activities and the operator’s stated requirements; no single bundle guarantees vendor approval.",
        ar: "تعالج ISO 9001 وISO 14001 وISO 45001 الجودة والبيئة والسلامة المهنية. وتضيف ISO 29001 متطلبات جودة خاصة بقطاع النفط والغاز. اختر ما يلائم أنشطتك ومتطلبات الجهة المشغّلة المحددة؛ فلا توجد مجموعة شهادات تضمن قبول المورد.",
      },
    },
    {
      q: { en: "Do I need ISO 9001 before the others?", ar: "هل أحتاج ايزو 9001 قبل غيره؟" },
      a: {
        en: "No. ISO 14001, ISO 45001 and ISO 22000 do not require you to hold an ISO 9001 certificate first. Choose the standard that addresses your management needs and buyer requirements.",
        ar: "لا. لا تشترط ISO 14001 أو ISO 45001 أو ISO 22000 الحصول أولاً على شهادة ISO 9001. اختر المواصفة التي تلبي احتياجات الإدارة في شركتك ومتطلبات المشتري.",
      },
    },
  ],
};

const standards = [
  {
    to: "/services/iso-9001",
    name: "ISO 9001",
    covers: { en: "Quality management", ar: "إدارة الجودة" },
    who: {
      en: "Organizations seeking a quality management system",
      ar: "المؤسسات التي تحتاج إلى نظام لإدارة الجودة",
    },
  },
  {
    to: "/services/iso-14001",
    name: "ISO 14001",
    covers: { en: "Environmental management", ar: "الإدارة البيئية" },
    who: {
      en: "Construction, infrastructure, exporters",
      ar: "الإنشاءات والبنية التحتية والمصدّرون",
    },
  },
  {
    to: "/services/iso-45001",
    name: "ISO 45001",
    covers: { en: "Occupational health & safety", ar: "الصحة والسلامة المهنية" },
    who: {
      en: "Construction, oil & gas, heavy industry",
      ar: "الإنشاءات والنفط والغاز والصناعة الثقيلة",
    },
  },
  {
    to: "/services/iso-22000",
    name: "ISO 22000",
    covers: { en: "Food safety", ar: "سلامة الغذاء" },
    who: { en: "Food producers and exporters", ar: "منتجو ومصدّرو الأغذية" },
  },
  {
    to: "/services/iso-29001",
    name: "ISO 29001",
    covers: { en: "Oil & gas quality", ar: "جودة النفط والغاز" },
    who: { en: "Petroleum supply chain and vendors", ar: "سلسلة توريد البترول والموردون" },
  },
];

export function WhichIsoBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "Choose your ISO standard around your activities, management needs and the buyer’s written requirements. A sector label alone does not determine which certificates you need. If you are new to certification, read our guide to ",
          ar: "اختر مواصفة الأيزو وفق أنشطتك واحتياجات الإدارة في شركتك ومتطلبات المشتري المكتوبة. فالقطاع الذي تعمل فيه لا يحدد وحده الشهادات التي تحتاجها. وإذا كنت تتعرف على شهادات الأيزو للمرة الأولى، فاقرأ دليلنا عن ",
        })}
        <LangLink
          to="/insights/iso-certification-cost-and-process-in-iraq"
          className="font-semibold text-primary hover:underline"
        >
          {t({
            en: "ISO certification cost and process in Iraq",
            ar: "تكلفة وإجراءات شهادة الأيزو في العراق",
          })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>

      <H2>
        {t({ en: "Match the standard to your activities", ar: "اختر المواصفة المناسبة لأنشطتك" })}
      </H2>
      <P>
        {t({
          en: "Each standard addresses a different management need. These examples help you compare the options; they are not a list of mandatory certificates for each sector. Follow the service link for details.",
          ar: "تعالج كل مواصفة جانباً مختلفاً من الإدارة. تساعدك الأمثلة التالية على مقارنة الخيارات، ولا تمثل قائمة بشهادات إلزامية لكل قطاع. اطّلع على صفحة الخدمة لمزيد من التفاصيل.",
        })}
      </P>
      <TableWrap>
        <thead className="bg-accent">
          <tr>
            <th className={thClass}>{t({ en: "Standard", ar: "المعيار" })}</th>
            <th className={thClass}>{t({ en: "Covers", ar: "يغطي" })}</th>
            <th className={thClass}>{t({ en: "Example activities", ar: "أمثلة على الأنشطة" })}</th>
          </tr>
        </thead>
        <tbody>
          {standards.map((row) => (
            <tr key={row.name} className="border-t border-border">
              <td className={tdClass}>
                <LangLink to={row.to} className="font-bold text-primary hover:underline">
                  {row.name}
                </LangLink>
              </td>
              <td className={tdClass}>{t(row.covers)}</td>
              <td className={tdClass}>{t(row.who)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <H2>
        {t({
          en: "ISO 9001 or ISO 22000 for a food business?",
          ar: "ISO 9001 أم ISO 22000 للمنشآت الغذائية؟",
        })}
      </H2>
      <P>
        {t({
          en: "ISO 9001 addresses quality management. ISO 22000 addresses food safety management, including the control of food safety hazards. A food producer, storage business or distributor should identify which need and customer requirement it is addressing before choosing one standard or a combined programme. The two certificates are not interchangeable.",
          ar: "تتناول ISO 9001 إدارة الجودة، بينما تتناول ISO 22000 إدارة سلامة الغذاء، بما فيها ضبط المخاطر المرتبطة بسلامته. ينبغي للمنشأة التي تنتج الأغذية أو تخزّنها أو توزّعها تحديد احتياجاتها ومتطلبات عملائها قبل اختيار مواصفة واحدة أو برنامج مشترك. ولا تحل إحدى الشهادتين محل الأخرى.",
        })}
      </P>
      <P>
        <a
          href="https://www.iso.org/standards/popular/iso-9000-family"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({ en: "ISO guidance on quality management", ar: "إرشادات ISO بشأن إدارة الجودة" })}
        </a>
        {t({ en: " and ", ar: " و" })}
        <a
          href="https://www.iso.org/iso-22000-food-safety-management.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({ en: "ISO 22000 food safety management", ar: "إدارة سلامة الغذاء وفق ISO 22000" })}
        </a>
        {t({ en: ".", ar: "." })}
      </P>

      <H2>{t({ en: "Combining standards", ar: "الجمع بين المواصفات" })}</H2>
      <P>
        {t({
          en: "A company may combine ISO 9001, ISO 14001 and ISO 45001 to manage quality, environmental impacts and workplace safety together. Food businesses may consider ISO 22000, while petroleum supply-chain organizations may consider ISO 29001. Confirm the need for each standard before agreeing the certification scope and audit plan.",
          ar: "يمكن للشركة الجمع بين ISO 9001 وISO 14001 وISO 45001 لإدارة الجودة والآثار البيئية والسلامة المهنية معاً. وقد تناسب ISO 22000 المنشآت الغذائية، وISO 29001 المؤسسات العاملة في سلسلة توريد النفط والغاز. حدّد حاجتك إلى كل مواصفة قبل الاتفاق على نطاق الشهادة وخطة التدقيق.",
        })}
      </P>

      <H2>
        {t({ en: "Confirm your requirements before starting", ar: "حدّد متطلباتك قبل البدء" })}
      </H2>
      <P>
        {t({ en: "Once you know the standard, clarify ", ar: "بعد تحديد المواصفة، وضّح " })}
        <LangLink
          to="/insights/iso-certification-scope-activities-sites-iraq"
          className="font-semibold text-primary underline underline-offset-2"
        >
          {t({
            en: "which activities and sites the certificate should cover",
            ar: "الأنشطة والمواقع المطلوب شمولها في نطاق الشهادة",
          })}
        </LangLink>
        {t({ en: " before requesting an offer.", ar: " قبل طلب عرض سعر." })}
      </P>
      <P>
        {t({
          en: "Send IraqCert your activities and the buyer’s stated requirements so we can scope the preparation support you need. The independent certification body conducts the certification audit and decides on issuance. ISO 29001 is outside partner body AQSR’s disclosed UAF-accredited scope; we confirm the issuing body and its scope before booking that audit. See ",
          ar: "أرسل إلى عراق سيرت تفاصيل أنشطتك ومتطلبات المشتري المحددة لنحدد دعم التحضير الذي تحتاجه. وتتولى جهة منح الشهادات المستقلة تدقيق منح الشهادة وقرار إصدارها. معيار ISO 29001 خارج نطاق اعتماد UAF المعلن للجهة الشريكة AQSR؛ لذا نؤكد الجهة المانحة ونطاق اعتمادها قبل حجز هذا التدقيق. اطّلع على ",
        })}
        <LangLink to="/services" className="font-semibold text-primary hover:underline">
          {t({ en: "our ISO certification services", ar: "خدمات شهادات الأيزو لدينا" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>
    </>
  );
}
